import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FileText, Shield, Users, Building, Target, TrendingUp, DollarSign, Zap, CheckCircle, ChevronRight, ArrowLeft } from 'lucide-react';
import { ComposedChart, Bar, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getCompanyData, isEnhancedCompany } from '../../utils/constants';
import { colors } from '../../utils/constants';

// Icon mapping since we can't store React components in JSON
const iconMap = {
  Shield,
  Users,
  Building,
  Target,
  TrendingUp,
  DollarSign,
  Zap,
  CheckCircle
};

const PortfolioCompany = ({ companyId, companyData: propCompanyData, onSectionChange }) => {
  // Add navigate hook for routing to analysis
  const navigate = useNavigate();
  
  // Use enhanced data structure if available, otherwise fall back to existing data
  const companyData = propCompanyData || getCompanyData(companyId);
  
  // Handle case where company data doesn't exist
  if (!companyData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Company Not Found</h2>
          <p className="text-gray-600">The requested company data could not be loaded.</p>
        </div>
      </div>
    );
  }

  // Check if this company uses the new enhanced structure
  const isEnhanced = isEnhancedCompany(companyId) || propCompanyData?.monthlyReturns;

  // Get the appropriate icon component
  const IconComponent = typeof companyData.icon === 'string' 
    ? iconMap[companyData.icon] 
    : companyData.icon;

  // Use the monthlyReturns data if available
  const monthlyReturnsData = companyData.monthlyReturns || [];

  // MetricsTable component with calculation column
  const MetricsTable = ({ metrics }) => {
    if (!metrics || metrics.length === 0) return null;

    return (
      <div className="bg-white p-8 rounded-xl border shadow-sm">
        <h3 className="text-xl font-semibold mb-6">Value Metrics Framework</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Metric</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Category</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Definition</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-900">Calculation Method</th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((metric, index) => (
                <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="py-4 px-4">
                    <div className="font-medium text-gray-900">{metric.name}</div>
                  </td>
                  <td className="py-4 px-4">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      metric.categoryColor === 'green' ? 'bg-green-100 text-green-800' :
                      metric.categoryColor === 'blue' ? 'bg-blue-100 text-blue-800' :
                      metric.categoryColor === 'purple' ? 'bg-purple-100 text-purple-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {metric.category}
                    </span>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm text-gray-600 max-w-md">
                      {metric.definition}
                    </div>
                  </td>
                  <td className="py-4 px-4">
                    <div className="text-sm text-gray-600 max-w-md">
                      {metric.calculationMethod || 'No calculation method specified'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => onSectionChange && onSectionChange('portfolio')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Back to portfolio"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
            {IconComponent && <IconComponent className="w-6 h-6 text-blue-600" />}
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900">{companyData.name}</h2>
            <p className="text-gray-600">{companyData.sector}</p>
          </div>
        </div>
      </div>

      {/* Deep Dive Analysis Banner */}
      {companyData.analysisId && (
        <div 
          className="relative overflow-hidden rounded-xl cursor-pointer group"
          onClick={() => navigate(`/analysis/${companyData.analysisId}`)}
        >
          <div className="absolute inset-0" style={{backgroundColor:colors.lightBlue}}></div>
          <div className="relative p-8 text-white">
            <div className="flex items-center justify-between">
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <FileText className="w-6 h-6" />
                  <span className="text-sm font-semibold uppercase tracking-wide">Deep Dive Analysis</span>
                </div>
                <h3 className="text-2xl font-bold">Comprehensive Performance Analysis</h3>
                <p className="text-blue-100 max-w-2xl">
                  Member retention metrics, engagement patterns, financial impact analysis, 
                  and strategic recommendations based on {companyData.name} data
                </p>
              </div>
              <div className="flex items-center space-x-2 text-white group-hover:translate-x-2 transition-transform">
                <span className="font-semibold">View Full Analysis</span>
                <ChevronRight className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div style={{backgroundColor:colors.blue}} className="text-white p-6 rounded-xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-blue-200">Investment Status:</span>
            <span className="text-white font-medium ml-2">{companyData.status}</span>
          </div>
          <div>
            <span className="text-blue-200">Service CU Integration:</span>
            <span className="text-white font-medium ml-2">{companyData.integration}</span>
          </div>
          <div>
            <span className="text-blue-200">Investment Date:</span>
            <span className="text-white font-medium ml-2">{companyData.investmentDate}</span>
          </div>
        </div>
      </div>
      
      <div className="bg-white p-8 rounded-xl border shadow-sm">
        <h3 className="text-lg font-semibold mb-6 text-blue-600 flex items-center space-x-2">
          <FileText className="w-5 h-5" />
          <span>Executive Summary</span>
        </h3>
        
        <div className="space-y-6">
          <div>
            <h4 className="font-medium text-gray-900 mb-2">Product Overview</h4>
            <p className="text-sm text-gray-600">{companyData.productOverview}</p>
          </div>
          
          {companyData.useCases && companyData.useCases.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Member Use Cases</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                {companyData.useCases.map((useCase, index) => (
                  <li key={index}>• {useCase}</li>
                ))}
              </ul>
            </div>
          )}

          {companyData.keyMilestones && companyData.keyMilestones.length > 0 && (
            <div>
              <h4 className="font-medium text-gray-900 mb-2">Key Milestones</h4>
              <div className="space-y-2">
                {companyData.keyMilestones.map((milestone, index) => (
                  <div key={index} className="flex justify-between items-center text-sm text-gray-600 py-2 border-b border-gray-100 last:border-b-0">
                    <div className="flex items-center space-x-3">
                      <span className="font-medium text-gray-900">{milestone.date}</span>
                      <span>{milestone.event}</span>
                    </div>
                    <span className="font-medium text-green-600">{milestone.amount}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced companies show investment performance summary */}
      {isEnhanced && companyData.returns && (
        <div className="p-8 rounded-xl text-white" style={{ backgroundColor: colors.blue }}>
          <h3 className="text-xl font-semibold mb-6">Investment Performance Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center p-4 bg-white/10 rounded-lg">
              <div className="text-2xl font-bold text-white">
                ${companyData.returns.efficiency.toLocaleString()}
              </div>
              <div className="text-sm text-blue-200">Efficiency Returns</div>
            </div>
            <div className="text-center p-4 bg-white/10 rounded-lg">
              <div className="text-2xl font-bold text-white">
                ${companyData.returns.income.toLocaleString()}
              </div>
              <div className="text-sm text-blue-200">Income Returns</div>
            </div>
            <div className="text-center p-4 bg-white/10 rounded-lg">
              <div className="text-2xl font-bold text-white">
                ${companyData.returns.contract.toLocaleString()}
              </div>
              <div className="text-sm text-blue-200">Contract Returns</div>
            </div>
            <div className="text-center p-4 bg-white/10 rounded-lg">
              <div className="text-2xl font-bold text-white">
                ${companyData.returns.total.toLocaleString()}
              </div>
              <div className="text-sm text-blue-200">Total Returns</div>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Returns Chart for enhanced companies */}
      {isEnhanced && monthlyReturnsData.length > 0 && (
        <div className="bg-white p-8 rounded-xl border shadow-sm">
          <h3 className="text-xl font-semibold mb-6">Monthly Returns Tracking</h3>
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={monthlyReturnsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                />
                <YAxis 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                  width={80}  // Increase width to make more room
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white',
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                  }}
                  formatter={(value, name) => [
                    `$${value.toLocaleString()}`,
                    name === 'efficiency' ? 'Efficiency Gains' :
                    name === 'income' ? 'Revenue Generation' : 
                    name === 'contract' ? 'Contract Savings' : 'Cumulative Total'
                  ]}
                  labelFormatter={(label) => `Month: ${label}`}
                />
                <Bar dataKey="efficiency" stackId="returns" fill="#3B82F6" name="efficiency" />
                <Bar dataKey="income" stackId="returns" fill="#DC2626" name="income" />
                <Bar dataKey="contract" stackId="returns" fill="#F59E0B" name="contract" />
                <Line 
                  type="monotone" 
                  dataKey="cumulative" 
                  stroke="#10B981" 
                  strokeWidth={3}
                  dot={{ fill: '#10B981', strokeWidth: 2, r: 4 }}
                  name="cumulative"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-[#3B82F6] rounded"></div>
              <span className="text-gray-600"><strong>Efficiency:</strong> Cost savings and operational improvements</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-[#DC2626] rounded"></div>
              <span className="text-gray-600"><strong>Income:</strong> Revenue sharing and fee generation</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-[#F59E0B] rounded"></div>
              <span className="text-gray-600"><strong>Contract:</strong> Negotiated discounts and savings</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-[#10B981] rounded"></div>
              <span className="text-gray-600"><strong>Cumulative:</strong> Running total of all returns</span>
            </div>
          </div>
        </div>
      )}

      {companyData.metrics && companyData.metrics.length > 0 && (
        <MetricsTable metrics={companyData.metrics} />
      )}
    </div>
  );
};

export default PortfolioCompany;